// counters.service.ts
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable, throwError, of, forkJoin, BehaviorSubject } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

interface Counts {
  workspacesCount: number;
  campaignsCount: number;
  sharedCampaignsCount: number;
  campaignsSharedForMeCount?: number;
  campaignsSharingCount?: number;
  ownedCampaignsCount?: number;
}

@Injectable({
  providedIn: 'root',
})
export class CountersService {
  private apiBaseUri = `${environment.apiUri}/counter`;
  private countsCache$: { [userId: string]: Observable<Counts> } = {};
  private countsSubjects: { [userId: string]: BehaviorSubject<Counts> } = {};

  constructor(private http: HttpClient) {}

  getCounts(userId: string, forceRefresh: boolean = false): Observable<Counts> {
    if (forceRefresh) {
      this.clearCache(userId);
    }

    if (!this.countsCache$[userId]) {
      console.log('Fetching counts for userId:', userId);

      this.countsCache$[userId] = forkJoin({
        workspacesCount: this.getWorkspacesCount(userId),
        campaignsCount: this.getCampaignsCount(userId),
        sharedCampaignsCount: this.getSharedCampaignsCount(userId),
        campaignsSharedForMeCount: this.getCampaignsSharedForMeCount(userId),
        campaignsSharingCount: this.getCampaignsSharingCount(userId),
        ownedCampaignsCount: this.getOwnedCampaignsCount(userId),
      }).pipe(
        shareReplay(1),
        catchError((error) => {
          console.error('Error fetching counts:', error);
          return of({
            workspacesCount: 0,
            campaignsCount: 0,
            sharedCampaignsCount: 0,
            campaignsSharedForMeCount: 0, // Default value for new field
            campaignsSharingCount: 0,     // Default value for new field
            ownedCampaignsCount: 0,       // Default value for new field
          });
        })
      );

      if (!this.countsSubjects[userId]) {
        this.countsSubjects[userId] = new BehaviorSubject<Counts>({
          workspacesCount: 0,
          campaignsCount: 0,
          sharedCampaignsCount: 0,
          campaignsSharedForMeCount: 0,
          campaignsSharingCount: 0,
          ownedCampaignsCount: 0,
        });
      }

      this.countsCache$[userId].subscribe((counts) => {
        this.countsSubjects[userId].next(counts);
      });
    } else {
      console.log('Using cached counts for userId:', userId);
    }

    return this.countsSubjects[userId].asObservable();
  }

  // Existing methods
  private getWorkspacesCount(userId: string): Observable<number> {
    const url = `${this.apiBaseUri}/workspaces/${userId}/count`;
    return this.getCount(url);
  }

  private getCampaignsCount(userId: string): Observable<number> {
    const url = `${this.apiBaseUri}/user/${userId}/campaigns/count`;
    return this.getCount(url);
  }

  private getSharedCampaignsCount(userId: string): Observable<number> {
    const url = `${this.apiBaseUri}/user/${userId}/shared/campaigns/count`;
    return this.getCount(url);
  }

  // New methods for new backend routes
  private getCampaignsSharedForMeCount(userId: string): Observable<number> {
    const url = `${this.apiBaseUri}/user/${userId}/sharedforme/campaigns/count`;
    return this.getCount(url);
  }

  private getCampaignsSharingCount(userId: string): Observable<number> {
    const url = `${this.apiBaseUri}/user/${userId}/sharing/campaigns/count`;
    return this.getCount(url);
  }

  private getOwnedCampaignsCount(userId: string): Observable<number> {
    const url = `${this.apiBaseUri}/user/${userId}/owned/campaigns/count`;
    return this.getCount(url);
  }

  // Common function to get any count
  private getCount(url: string): Observable<number> {
    console.log('Fetching count from URL:', url);
    return this.http.get<{ count: number }>(url, { withCredentials: true }).pipe(
      map((response) => response.count),
      catchError(this.handleError)
    );
  }

  // Common error handling for all requests
  private handleError(error: any) {
    console.error('API Error:', error);
    return throwError(() => 'Something went wrong; please try again later.');
  }

  clearCache(userId: string): void {
    delete this.countsCache$[userId];
    if (this.countsSubjects[userId]) {
      this.countsSubjects[userId].next({
        workspacesCount: 0,
        campaignsCount: 0,
        sharedCampaignsCount: 0,
        campaignsSharedForMeCount: 0,
        campaignsSharingCount: 0,
        ownedCampaignsCount: 0,
      });
    }
  }
}


// import { Injectable, signal } from '@angular/core';
// import { HttpClient } from '@angular/common/http';
// import { environment } from 'src/environments/environment';

// // interface Counts {
// //   workspacesCount: number;
// //   campaignsCount: number;
// //   sharedCampaignsCount: number;
// //   campaignsSharedForMeCount?: number;
// //   campaignsSharingCount?: number;
// //   ownedCampaignsCount?: number;
// // }

// export interface Counts {
//   workspacesCount: number;
//   campaignsCount: number;
//   sharedCampaignsCount: number;
//   campaignsSharedForMeCount?: number;
//   campaignsSharingCount?: number;
//   ownedCampaignsCount?: number;
// }


// @Injectable({
//   providedIn: 'root',
// })
// export class CountersService {
//   private apiBaseUri = `${environment.apiUri}/counter`;

//   // Map to store signals for each user
//   private counts = new Map<string, ReturnType<typeof signal>>();

//   constructor(private http: HttpClient) {}

//   /**
//    * Get counts for a user
//    * @param userId - The ID of the user
//    * @param forceRefresh - Whether to force refresh the data
//    * @returns Signal for the user's counts
//    */
//   getCounts(userId: string, forceRefresh: boolean = false) {
//     if (forceRefresh || !this.counts.has(userId)) {
//       // If forceRefresh is true or counts don't exist, fetch data
//       this.fetchCounts(userId);
//     }

//     // Return the signal for this user
//     if (!this.counts.has(userId)) {
//       // Create a signal with default data if not already present
//       this.counts.set(userId, signal<Counts>(this.getDefaultCounts()));
//     }

//     return this.counts.get(userId)!;
//   }

//   /**
//    * Fetch counts from the backend for a specific user
//    * @param userId - The ID of the user
//    */
//   private fetchCounts(userId: string): void {
//     const defaultCounts = this.getDefaultCounts();

//     this.counts.set(userId, signal<Counts>(defaultCounts)); // Initialize signal with default values

//     Promise.all([
//       this.getWorkspacesCount(userId),
//       this.getCampaignsCount(userId),
//       this.getSharedCampaignsCount(userId),
//       this.getCampaignsSharedForMeCount(userId),
//       this.getCampaignsSharingCount(userId),
//       this.getOwnedCampaignsCount(userId),
//     ])
//       .then(
//         ([
//           workspacesCount,
//           campaignsCount,
//           sharedCampaignsCount,
//           campaignsSharedForMeCount,
//           campaignsSharingCount,
//           ownedCampaignsCount,
//         ]) => {
//           const newCounts: Counts = {
//             workspacesCount,
//             campaignsCount,
//             sharedCampaignsCount,
//             campaignsSharedForMeCount,
//             campaignsSharingCount,
//             ownedCampaignsCount,
//           };

//           // Update the signal with the fetched data
//           this.counts.get(userId)!.set(newCounts);
//         }
//       )
//       .catch((error) => {
//         console.error('Error fetching counts:', error);
//         // Handle error if needed (optional)
//       });
//   }

//   /**
//    * Default counts structure
//    * @returns Default counts
//    */
//   private getDefaultCounts(): Counts {
//     return {
//       workspacesCount: 0,
//       campaignsCount: 0,
//       sharedCampaignsCount: 0,
//       campaignsSharedForMeCount: 0,
//       campaignsSharingCount: 0,
//       ownedCampaignsCount: 0,
//     };
//   }

//   // Methods to fetch counts from backend
//   private getWorkspacesCount(userId: string): Promise<number> {
//     const url = `${this.apiBaseUri}/workspaces/${userId}/count`;
//     return this.getCount(url);
//   }

//   private getCampaignsCount(userId: string): Promise<number> {
//     const url = `${this.apiBaseUri}/user/${userId}/campaigns/count`;
//     return this.getCount(url);
//   }

//   private getSharedCampaignsCount(userId: string): Promise<number> {
//     const url = `${this.apiBaseUri}/user/${userId}/shared/campaigns/count`;
//     return this.getCount(url);
//   }

//   private getCampaignsSharedForMeCount(userId: string): Promise<number> {
//     const url = `${this.apiBaseUri}/user/${userId}/sharedforme/campaigns/count`;
//     return this.getCount(url);
//   }

//   private getCampaignsSharingCount(userId: string): Promise<number> {
//     const url = `${this.apiBaseUri}/user/${userId}/sharing/campaigns/count`;
//     return this.getCount(url);
//   }

//   private getOwnedCampaignsCount(userId: string): Promise<number> {
//     const url = `${this.apiBaseUri}/user/${userId}/owned/campaigns/count`;
//     return this.getCount(url);
//   }

//   // Helper function to fetch count from backend
//   private getCount(url: string): Promise<number> {
//     console.log('Fetching count from URL:', url);
//     return this.http
//       .get<{ count?: number }>(url, { withCredentials: true })
//       .toPromise()
//       .then((response) => {
//         if (response && typeof response.count === 'number') {
//           return response.count;
//         }
//         console.warn(`Response from ${url} is undefined or missing 'count'. Defaulting to 0.`);
//         return 0; // Default to 0 if response or count is invalid
//       })
//       .catch((error) => {
//         console.error('Error fetching count from:', url, error);
//         return 0; // Default to 0 on error
//       });
//   }
  

//   /**
//    * Clear cached counts for a user
//    * @param userId - The ID of the user
//    */
//   clearCache(userId: string): void {
//     if (this.counts.has(userId)) {
//       this.counts.get(userId)!.set(this.getDefaultCounts());
//     }
//   }
// }
